import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Loader2, ExternalLink, ShoppingCart, Truck, AlertCircle, AlertTriangle, CheckCircle, RefreshCw,
} from 'lucide-react';
import { useStauff } from '../hooks/useStauff';
import { useOrderingEnabled } from '../hooks/useOrderingEnabled';
import { showNotification, showError } from '../utils/notificationHandler';
import { formatCurrency } from '../utils/currency';
import Button from './shared/Button';
import StatusBadge from './shared/StatusBadge';
import { QuantityInput } from './shared/ProductParts';

// Each location is judged against the quantity asked for, so 4 of 10 reads "Partial".
export const stockBadge = (available, requested) => {
  if (available <= 0) return { tone: 'danger', label: 'Out of stock' };
  if (available < requested) return { tone: 'warning', label: 'Partial' };
  return { tone: 'success', label: 'In stock' };
};

const LocationTile = ({ title, subtitle, available, requested, children }) => {
  const { tone, label } = stockBadge(available, requested);
  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          <p className="text-xs text-gray-600">{subtitle}</p>
        </div>
        <StatusBadge tone={tone}>{label}</StatusBadge>
      </div>
      <p className="mt-3 text-gray-900">
        <span className="text-xl font-bold tabular-nums">{available}</span>
        <span className="text-sm text-gray-600"> available</span>
      </p>
      {children}
    </div>
  );
};

const Summary = ({ combined, requested }) => {
  const ok = combined.canFulfillRequest;
  const Icon = ok ? CheckCircle : AlertTriangle;
  return (
    <div
      role="status"
      className={`p-3 rounded-lg border flex items-start gap-2 text-sm ${
        ok ? 'border-green-200 bg-green-50 text-green-900' : 'border-orange-200 bg-orange-50 text-orange-900'
      }`}
    >
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${ok ? 'text-green-600' : 'text-orange-600'}`} aria-hidden="true" />
      <div>
        <p className="font-semibold tabular-nums">
          {ok ? `All ${requested} can be supplied` : `Only ${combined.totalAvailable} of ${requested} can be supplied now`}
        </p>
        {combined.fulfillmentStrategy?.description && (
          <p className="mt-0.5">{combined.fulfillmentStrategy.description}</p>
        )}
      </div>
    </div>
  );
};

const StauffStockModal = ({
  isOpen,
  onClose,
  partNumber,
  autoCheck = false,
  onAddToCart,
  initialQuantity = 1,
}) => {
  const [quantity, setQuantity] = useState(initialQuantity);
  const { enabled: orderingEnabled } = useOrderingEnabled();
  const { loading, error, availability, checkCombinedStock, clearResults } = useStauff();

  useEffect(() => {
    // initialQuantity, not quantity: the quantity state hasn't caught up with this open yet.
    if (isOpen && autoCheck && partNumber) {
      handleStockCheck(initialQuantity);
    }
  }, [isOpen, autoCheck, partNumber]);

  useEffect(() => {
    if (!isOpen) {
      clearResults();
      setQuantity(initialQuantity);
    }
  }, [isOpen, clearResults, initialQuantity]);

  useEffect(() => {
    if (isOpen) {
      setQuantity(initialQuantity);
    }
  }, [isOpen, initialQuantity]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const handleStockCheck = async (qty = quantity) => {
    try {
      await checkCombinedStock(partNumber, qty);
    } catch (err) {
      showError(err, 'Failed to check combined stock');
    }
  };

  const handleAddToCart = () => {
    if (availability && onAddToCart) {
      const cartItem = {
        partNumber: availability.sku,
        description: availability.name,
        quantity: quantity,
        price: availability.trackingPrice ? availability.trackingPrice / 100 : null,
        supplier: 'Stauff',
        isStauff: true
      };
      onAddToCart(cartItem);
      showNotification(`${quantity} ${availability.name} added to cart`, 'success');
    }
  };

  const requested = availability?.requestedQuantity ?? quantity;
  const local = availability?.localStock;
  const stauff = availability?.availability;
  const name = availability?.name || local?.description;
  const pricing = availability?.pricing || {};
  const stauffPrice = pricing.listPrice
    || (availability?.trackingPrice ? formatCurrency(availability.trackingPrice / 100) : null);
  const canAddToCart = availability?.canBeAddedToCart && onAddToCart && orderingEnabled && !loading;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4"
          onClick={onClose}
        >
          {/* Bottom sheet on mobile, centred dialog from sm up */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="stauff-stock-title"
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            className="bg-white w-full sm:max-w-2xl max-h-[90dvh] flex flex-col rounded-t-xl sm:rounded-lg shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 id="stauff-stock-title" className="text-lg font-semibold text-gray-900">
                  Stauff stock check
                </h2>
                <p className="text-sm text-gray-600 mt-0.5 break-words">
                  <span className="font-mono font-medium text-gray-900 break-all">{partNumber}</span>
                  {name && ` · ${name}`}
                </p>
              </div>
              <Button
                variant="ghost"
                ariaLabel="Close"
                onClick={onClose}
                className="-mr-2 -mt-1 flex-shrink-0"
                icon={<X className="h-5 w-5" aria-hidden="true" />}
              />
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-5">
              <form
                onSubmit={(e) => { e.preventDefault(); handleStockCheck(); }}
                className="flex flex-wrap items-end gap-3"
              >
                <QuantityInput id="stauff-quantity" value={quantity} onChange={setQuantity} />
                <Button
                  type="submit"
                  size="lg"
                  variant={availability ? 'secondary' : 'primary'}
                  loading={loading}
                  icon={availability
                    ? <RefreshCw className="w-4 h-4" aria-hidden="true" />
                    : <Truck className="w-4 h-4" aria-hidden="true" />}
                >
                  {availability ? 'Re-check' : 'Check stock'}
                </Button>
              </form>

              {loading && (
                <div role="status" className="py-8 flex flex-col items-center text-center">
                  <Loader2 className="w-8 h-8 text-gray-400 animate-spin" aria-hidden="true" />
                  <p className="mt-3 text-sm font-medium text-gray-900 tabular-nums">
                    Checking stock for {quantity}…
                  </p>
                  <p className="text-sm text-gray-600">Perth and Stauff eastern states</p>
                </div>
              )}

              {error && !loading && (
                <div role="alert" className="p-3 rounded-lg border border-red-200 bg-red-50 flex items-start gap-2 text-sm text-red-900">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <p><span className="font-semibold">Couldn't check stock.</span> {error}</p>
                </div>
              )}

              {availability && !loading && (
                <div className="space-y-4">
                  {availability.combinedAvailability && (
                    <Summary combined={availability.combinedAvailability} requested={requested} />
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {local && (
                      <LocationTile title="Perth" subtitle="MHS stock" available={local.available || 0} requested={requested} />
                    )}
                    {stauff?.status && (
                      <LocationTile title="Eastern states" subtitle="Stauff stock" available={stauff.quantity || 0} requested={requested}>
                        <p className="mt-2 text-sm text-gray-600 flex items-center gap-1.5">
                          {stauff.hasTruckDelivery
                            ? <><Truck className="w-4 h-4" aria-hidden="true" />Available for delivery</>
                            : <><AlertCircle className="w-4 h-4" aria-hidden="true" />Contact us for delivery</>}
                        </p>
                        {stauff.stockistLink && (
                          <a
                            href={stauff.stockistLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 underline decoration-brand decoration-2 underline-offset-4 hover:decoration-brand-hover"
                          >
                            Find nearest STAUFF stockist
                            <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
                          </a>
                        )}
                      </LocationTile>
                    )}
                  </div>

                  {stauffPrice && (
                    <p className="text-sm text-gray-600">
                      Stauff list price{' '}
                      <span className="font-medium text-gray-900 tabular-nums">{stauffPrice}</span>
                      {pricing.unit && ` ${pricing.unit}`}
                      {pricing.additionalInfo && ` · ${pricing.additionalInfo}`}
                    </p>
                  )}

                  {availability.bom?.length > 0 && (
                    <p className="text-sm text-gray-600">
                      Includes {availability.bom.length} components (bill of materials).
                    </p>
                  )}
                </div>
              )}

              <p className="text-xs text-gray-500">
                Stock levels are indicative. If in doubt, please contact us.
              </p>
            </div>

            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 sm:rounded-b-lg flex gap-2 sm:justify-end">
              <Button variant="secondary" onClick={onClose} className="flex-1 sm:flex-none">
                Close
              </Button>
              {canAddToCart && (
                <Button
                  onClick={handleAddToCart}
                  className="flex-1 sm:flex-none font-semibold"
                  icon={<ShoppingCart className="w-4 h-4" aria-hidden="true" />}
                >
                  Add {quantity} to Cart
                </Button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default StauffStockModal;
